/**
 * Rebuilding an innings that was played on someone else's phone.
 *
 * This is the payoff of the storage design. A room holds seeds, squads and ~1 KB
 * of decisions — never ball-by-ball data — because the engine is deterministic:
 * feeding the same decisions back into `simulateInnings` reproduces all 240
 * deliveries exactly, including who was caught by whom.
 *
 * So the opponent's scorecard is not fetched. It is re-derived here, from the
 * same inputs the server used to verify it. If this ever disagreed with the
 * server, the room would have rejected the innings in the first place.
 */

import {
  simulateInnings,
  SUPER_OVER_FORMAT,
  type Answer,
  type Coefficients,
  type DraftPlayer,
  type InningsResult,
  type Player,
} from '@ipl-sim/engine-cricket';
import type { RoomView } from './room.svelte';

export interface ReplayedInnings {
  readonly name: string;
  readonly result: InningsResult;
}

function run(args: {
  seed: string;
  inningsIndex: 0 | 1;
  battingXI: readonly Player[];
  bowlingXI: readonly Player[];
  decisions: readonly Answer[];
  coefficients: Coefficients;
  target?: number;
  superOver?: boolean;
}): InningsResult | null {
  const generator = simulateInnings({
    seed: args.seed,
    inningsIndex: args.inningsIndex,
    battingXI: args.battingXI,
    bowlingXI: args.bowlingXI,
    coefficients: args.coefficients,
    target: args.target,
    format: args.superOver ? SUPER_OVER_FORMAT : undefined,
  });

  let step = generator.next();
  let i = 0;
  while (!step.done) {
    const answer = args.decisions[i++];
    // A log that runs out means this client and the server disagree about the
    // innings. Returning null shows totals only rather than a wrong scorecard.
    if (!answer) return null;
    step = generator.next(answer);
  }
  return step.value;
}

/**
 * Both innings of a finished room, as full scorecards.
 *
 * Returns an empty list if anything cannot be reconstructed — a missing squad,
 * a truncated log, a coefficient set from another version. A partial scorecard
 * would be worse than none: every number on it is presented as fact.
 */
export function replayRoom(view: RoomView, coefficients: Coefficients): ReplayedInnings[] {
  if (!view.yourXI || !view.opponentXI) return [];

  /** The team name for a side, whichever side the viewer happens to be. */
  const teamName = (side: string): string =>
    (side === 'host' ? view.host.teamName : view.guest?.teamName) ?? 'Opponent';

  const out: ReplayedInnings[] = [];

  view.innings.forEach((innings, index) => {
    const batting = (innings.side === view.you ? view.yourXI : view.opponentXI) as
      | readonly DraftPlayer[]
      | null;
    const bowling = (innings.side === view.you ? view.opponentXI : view.yourXI) as
      | readonly DraftPlayer[]
      | null;
    if (!batting || !bowling) return;

    const result = run({
      seed: view.seed,
      inningsIndex: index as 0 | 1,
      battingXI: batting as readonly Player[],
      bowlingXI: bowling as readonly Player[],
      decisions: innings.decisions,
      coefficients,
      target: index === 1 ? view.innings[0].runs + 1 : undefined,
    });

    if (result) out.push({ name: teamName(innings.side), result });
  });

  // Only complete scorecards are worth showing.
  return out.length === view.innings.length ? out : [];
}
